import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Users,
  UserPlus,
  Plus,
  CircleCheck,
  Circle,
  Loader2,
} from 'lucide-react'
import { attendancePresenceVariant } from '@/lib/status-variants'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'

interface User {
  id: string
  name: string
  email?: string | null
}

interface ExternalGuest {
  id?: string
  name: string
  email?: string | null
  company?: string | null
  present?: boolean
  orderIndex?: number
}

interface AttendeeSnapshot {
  userId: string
  name: string
  email: string | null
  present?: boolean
}

interface Props {
  meeting: {
    id: string
    status: string
    attendees: User[]
    externalGuests: ExternalGuest[]
    presentAttendeeIds?: string[]
  }
  projectId: string
  readOnly?: boolean
  snapshotAttendees?: AttendeeSnapshot[]
  snapshotGuests?: ExternalGuest[]
  onPendingChange?: (pending: boolean) => void
  // MEETINGS.EDIT — setAttendeePresence/setExternalGuestPresence/
  // addMeetingAttendeePresent/addMeetingExternalGuestPresent are all EDIT.
  canEdit: boolean
}

const MeetingAttendancePanel: React.FC<Props> = ({
  meeting,
  projectId,
  readOnly,
  snapshotAttendees,
  snapshotGuests,
  onPendingChange,
  canEdit,
}) => {
  const { t } = useTranslation()
  // isLive is the business-state check (independent of permissions) — presence
  // tracking is meaningful, and should stay visible per "no read/view hidden",
  // whenever the meeting is in progress or being viewed as a finalized snapshot.
  const isLive = !readOnly && meeting.status === 'IN_PROGRESS'
  const showPresenceColumn = isLive || readOnly
  // editable additionally requires MEETINGS.EDIT — this only gates the
  // interactive checkbox/add-attendee triggers, not the presence view itself.
  const editable = isLive && canEdit

  const { data: projectData } = useQuery(Query.PROJECT, {
    variables: { id: projectId },
    skip: !editable || !projectId,
  })
  const projectUsers: User[] = (projectData?.project?.users || []).map((pu: any) => ({
    id: pu.user.id,
    name: pu.user.name,
    email: pu.user.email,
  }))

  const [setAttendeePresence] = useMutation(Mutation.SET_ATTENDEE_PRESENCE)
  const [setGuestPresence] = useMutation(Mutation.SET_EXTERNAL_GUEST_PRESENCE)
  const [addAttendeePresent, { loading: addingAttendee }] = useMutation(
    Mutation.ADD_MEETING_ATTENDEE_PRESENT
  )
  const [addGuestPresent, { loading: addingGuest }] = useMutation(
    Mutation.ADD_MEETING_EXTERNAL_GUEST_PRESENT
  )

  const pendingCountRef = useRef(0)
  const wrapPending = async <T,>(fn: () => Promise<T>): Promise<T> => {
    if (pendingCountRef.current === 0) onPendingChange?.(true)
    pendingCountRef.current += 1
    try {
      return await fn()
    } finally {
      pendingCountRef.current -= 1
      if (pendingCountRef.current === 0) onPendingChange?.(false)
    }
  }

  const [addPickerOpen, setAddPickerOpen] = useState(false)
  const [addQuery, setAddQuery] = useState('')

  const [addGuestOpen, setAddGuestOpen] = useState(false)
  const [newGuestName, setNewGuestName] = useState('')
  const [newGuestEmail, setNewGuestEmail] = useState('')
  const [newGuestCompany, setNewGuestCompany] = useState('')

  const attendees: AttendeeSnapshot[] = useMemo(() => {
    if (readOnly && snapshotAttendees) return snapshotAttendees
    const presentIds = meeting.presentAttendeeIds || []
    return (meeting.attendees || []).map((u) => ({
      userId: u.id,
      name: u.name || '',
      email: u.email ?? null,
      present: presentIds.includes(u.id),
    }))
  }, [readOnly, snapshotAttendees, meeting.attendees, meeting.presentAttendeeIds])

  const externalGuests: ExternalGuest[] = useMemo(() => {
    if (readOnly && snapshotGuests) return snapshotGuests
    return meeting.externalGuests ?? []
  }, [readOnly, snapshotGuests, meeting.externalGuests])

  const presentCount =
    attendees.filter((a) => a.present).length +
    externalGuests.filter((g) => g.present).length
  const totalCount = attendees.length + externalGuests.length

  const availableUsers = useMemo(() => {
    const existingIds = new Set(attendees.map((a) => a.userId))
    return projectUsers.filter((u) => {
      if (existingIds.has(u.id)) return false
      if (!addQuery) return true
      const q = addQuery.toLowerCase()
      return (
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
      )
    })
  }, [projectUsers, attendees, addQuery])

  const handleToggleAttendee = async (userId: string, next: boolean) => {
    const presentIds = meeting.presentAttendeeIds || []
    const optimisticIds = next
      ? Array.from(new Set([...presentIds, userId]))
      : presentIds.filter((id) => id !== userId)
    try {
      await wrapPending(() =>
        setAttendeePresence({
          variables: { meetingId: meeting.id, userId, present: next },
          optimisticResponse: {
            __typename: 'Mutation',
            setAttendeePresence: {
              __typename: 'Meeting',
              id: meeting.id,
              presentAttendeeIds: optimisticIds,
            },
          },
        })
      )
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    }
  }

  const handleToggleGuest = async (guestId: string, next: boolean) => {
    const guests = meeting.externalGuests ?? []
    const optimisticGuests = guests.map((g) =>
      g.id === guestId ? { ...g, present: next } : g
    )
    try {
      await wrapPending(() =>
        setGuestPresence({
          variables: { meetingId: meeting.id, guestId, present: next },
          optimisticResponse: {
            __typename: 'Mutation',
            setExternalGuestPresence: {
              __typename: 'Meeting',
              id: meeting.id,
              externalGuests: optimisticGuests.map((g) => ({
                __typename: 'MeetingExternalGuest',
                ...g,
              })),
            },
          },
        })
      )
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    }
  }

  const handleAddAttendee = async (userId: string) => {
    const user = projectUsers.find((u) => u.id === userId)
    const presentIds = meeting.presentAttendeeIds || []
    const optimisticPresent = Array.from(new Set([...presentIds, userId]))
    const optimisticAttendees = [
      ...(meeting.attendees || []),
      ...(user ? [{ __typename: 'User', id: user.id, name: user.name, email: user.email ?? null }] : []),
    ]
    try {
      await wrapPending(() =>
        addAttendeePresent({
          variables: { meetingId: meeting.id, userId },
          optimisticResponse: {
            __typename: 'Mutation',
            addMeetingAttendeePresent: {
              __typename: 'Meeting',
              id: meeting.id,
              presentAttendeeIds: optimisticPresent,
              attendeeCount: optimisticAttendees.length,
              attendees: optimisticAttendees,
            },
          },
        })
      )
      setAddPickerOpen(false)
      setAddQuery('')
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    }
  }

  const handleAddGuest = async () => {
    const name = newGuestName.trim()
    if (!name) return
    const guest = {
      name,
      email: newGuestEmail.trim() || null,
      company: newGuestCompany.trim() || null,
    }
    const existing = meeting.externalGuests ?? []
    const maxOrder = existing.reduce((m, g) => Math.max(m, g.orderIndex ?? 0), -1)
    try {
      await wrapPending(() =>
        addGuestPresent({
          variables: { meetingId: meeting.id, guest },
          optimisticResponse: {
            __typename: 'Mutation',
            addMeetingExternalGuestPresent: {
              __typename: 'Meeting',
              id: meeting.id,
              externalGuests: [
                ...existing.map((g) => ({ __typename: 'MeetingExternalGuest', ...g })),
                {
                  __typename: 'MeetingExternalGuest',
                  id: `temp-${Date.now()}`,
                  ...guest,
                  present: true,
                  orderIndex: maxOrder + 1,
                },
              ],
            },
          },
        })
      )
      setAddGuestOpen(false)
      setNewGuestName('')
      setNewGuestEmail('')
      setNewGuestCompany('')
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          {t('Meeting_section_attendees')}
          <span className="text-xs text-muted-foreground font-normal ml-auto">
            {showPresenceColumn ? `${presentCount} / ${totalCount}` : totalCount}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div>
          <div className="text-muted-foreground text-xs mb-1 flex items-center justify-between gap-1">
            <span>{t('Meeting_attendees_internal')}</span>
            {editable && (
              <Popover open={addPickerOpen} onOpenChange={setAddPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    disabled={addingAttendee}
                  >
                    {addingAttendee ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Plus className="h-3 w-3" />
                    )}
                    {t('Meeting_attendees_add')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="end">
                  <div className="p-2 border-b">
                    <Input
                      placeholder={t('Meeting_attendees_search_placeholder')}
                      value={addQuery}
                      onChange={(e) => setAddQuery(e.target.value)}
                      className="h-8 text-sm"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto py-1">
                    {availableUsers.length === 0 ? (
                      <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                        {t('Meeting_attendees_no_users')}
                      </div>
                    ) : (
                      availableUsers.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => handleAddAttendee(u.id)}
                          disabled={addingAttendee}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted/60 text-left disabled:opacity-50"
                        >
                          <UserPlus className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="truncate">{u.name}</div>
                            {u.email && (
                              <div className="text-xs text-muted-foreground truncate">
                                {u.email}
                              </div>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
          {attendees.length === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : editable ? (
            <div className="space-y-1">
              {attendees.map((a) => (
                <label
                  key={a.userId}
                  className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer hover:bg-muted/40"
                >
                  <Checkbox
                    checked={!!a.present}
                    onCheckedChange={(v) => handleToggleAttendee(a.userId, !!v)}
                  />
                  <span className="flex-1 truncate">{a.name}</span>
                </label>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1">
              {attendees.map((a) => (
                <Badge
                  key={a.userId}
                  variant={showPresenceColumn ? attendancePresenceVariant(a.present) : 'outline'}
                  className="gap-1"
                >
                  {showPresenceColumn &&
                    (a.present ? (
                      <CircleCheck className="h-3 w-3" />
                    ) : (
                      <Circle className="h-3 w-3" />
                    ))}
                  {a.name}
                </Badge>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-muted-foreground text-xs mb-1 flex items-center justify-between gap-1">
            <span className="flex items-center gap-1">
              <UserPlus className="h-3 w-3" />
              {t('Meeting_attendees_external')}
            </span>
            {editable && (
              <Popover open={addGuestOpen} onOpenChange={setAddGuestOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    disabled={addingGuest}
                  >
                    {addingGuest ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Plus className="h-3 w-3" />
                    )}
                    {t('Meeting_guest_add')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-3 space-y-2" align="end">
                  <Input
                    placeholder={t('Meeting_guest_name')}
                    value={newGuestName}
                    onChange={(e) => setNewGuestName(e.target.value)}
                    className="h-8 text-sm"
                    autoFocus
                  />
                  <Input
                    placeholder={t('Meeting_guest_email')}
                    value={newGuestEmail}
                    onChange={(e) => setNewGuestEmail(e.target.value)}
                    className="h-8 text-sm"
                  />
                  <Input
                    placeholder={t('Meeting_guest_company')}
                    value={newGuestCompany}
                    onChange={(e) => setNewGuestCompany(e.target.value)}
                    className="h-8 text-sm"
                  />
                  <div className="flex justify-end gap-2 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAddGuestOpen(false)}
                      disabled={addingGuest}
                    >
                      {t('Common_cancel')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleAddGuest}
                      disabled={!newGuestName.trim() || addingGuest}
                    >
                      {addingGuest && <Loader2 className="h-3 w-3 animate-spin" />}
                      {t('Common_add')}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
          {externalGuests.length === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : editable ? (
            <div className="space-y-1">
              {externalGuests.map((g, idx) => (
                <label
                  key={g.id ?? idx}
                  className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer hover:bg-muted/40"
                >
                  <Checkbox
                    checked={!!g.present}
                    onCheckedChange={(v) => g.id && handleToggleGuest(g.id, !!v)}
                  />
                  <span className="flex-1 truncate">
                    {g.name}
                    {g.company && (
                      <span className="text-muted-foreground"> · {g.company}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1">
              {externalGuests.map((g, idx) => (
                <Badge
                  key={g.id ?? idx}
                  variant={showPresenceColumn ? attendancePresenceVariant(g.present) : 'outline'}
                  className="gap-1"
                >
                  {showPresenceColumn &&
                    (g.present ? (
                      <CircleCheck className="h-3 w-3" />
                    ) : (
                      <Circle className="h-3 w-3" />
                    ))}
                  {g.name}
                  {g.company && <span className="text-muted-foreground">· {g.company}</span>}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export default MeetingAttendancePanel
